// Voice picker. Lists curated voices for the chosen TTS provider. Each card
// has a play button that hits /api/preview to play a short snippet inline.

import { useEffect, useRef, useState } from 'react';
import { voices, type TtsProvider, type VoiceDescriptor } from '../api.js';
import { Spinner } from './ui/spinner.js';

interface Props {
  provider: TtsProvider;
  selectedVoiceId: string | null;
  onSelect: (voiceId: string) => void;
}

export function VoicePicker({ provider, selectedVoiceId, onSelect }: Props) {
  const [list, setList] = useState<VoiceDescriptor[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setList(null);
    setError(null);
    voices.list(provider)
      .then((r) => { if (!cancelled) setList(r.voices); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.code === 'ADAPTER_NOT_IMPLEMENTED') {
          setError(`The ${provider} adapter isn’t implemented yet. Pick another provider.`);
        } else {
          setError(err?.message ?? 'Failed to load voices.');
        }
      });
    return () => {
      cancelled = true;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [provider]);

  async function preview(v: VoiceDescriptor) {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPreviewing(v.id);
    try {
      const res = await fetch('/api/preview', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider, voice_id: v.id }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error ?? `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => {
        setPreviewing((cur) => (cur === v.id ? null : cur));
        URL.revokeObjectURL(url);
      };
      await a.play();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed.');
      setPreviewing(null);
    }
  }

  if (error) {
    return (
      <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2">
        {error}
      </div>
    );
  }

  if (!list) {
    return <Spinner size={16} label="Loading voices…" className="text-muted" />;
  }

  if (list.length === 0) {
    return <div className="text-sm text-muted">No voices available.</div>;
  }

  return (
    <ul className="space-y-2">
      {list.map((v) => {
        const sel = v.id === selectedVoiceId;
        const playing = previewing === v.id;
        return (
          <li key={v.id}>
            <button
              type="button"
              onClick={() => onSelect(v.id)}
              className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${
                sel
                  ? 'border-accent bg-accent/5'
                  : 'border-soft bg-white hover:border-muted/40'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-ink truncate">{v.display_name}</div>
                  <div className="text-xs text-muted/80 mt-0.5 truncate">{v.description}</div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void preview(v);
                  }}
                  disabled={playing}
                  className={`shrink-0 w-8 h-8 rounded-full grid place-items-center transition-colors ${
                    playing
                      ? 'bg-accent/20 text-accent'
                      : 'bg-soft text-muted hover:bg-soft/70 hover:text-ink'
                  }`}
                  title="Preview voice"
                  aria-label={`Preview ${v.display_name}`}
                >
                  {playing ? (
                    <svg width="14" height="14" viewBox="0 0 14 14"><circle cx="7" cy="7" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="20" className="animate-spin origin-center" /></svg>
                  ) : (
                    <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 1l7 4-7 4z" fill="currentColor" /></svg>
                  )}
                </button>
              </div>
              {v.style_tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {v.style_tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-soft/60 text-muted rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
